import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';
import { CircularProgress, Paper, withStyles } from '@material-ui/core';
import { Button } from 'embeds/components';

import DialogConfirmation from '../DialogConfirmation';

const styles = ({
  custom: {
    palette: { colorDanger },
  },
  spacing: { unit },
  palette: {
    common: { white },
  },
}) => ({
  buttonsLeft: {
    float: 'left',
    display: 'flex',
    alignSelf: 'flex-start',
  },
  buttonsRight: {
    display: 'flex',
    alignSelf: 'flex-end',
    gap: `${unit}px`,
  },
  deleteButton: {
    color: colorDanger,
  },
  footer: {
    padding: `${unit * 2}px ${unit * 3}px`,
    backgroundColor: white,
    zIndex: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    left: 0,
    right: 0,
    bottom: 0,
    position: 'fixed',
  },
  form: {
    flex: 1,
    flexDirection: 'column',
    overflowY: 'auto',
    // This property is necessary to fix a known bug between
    // material-ui-next Grid component and IE11.
    overflowX: 'hidden',
    padding: `${unit * 3}px ${unit * 3}px 100px ${unit * 3}px`,
  },
  loadingProgress: {
    position: 'fixed',
    top: '50%',
    left: '55%',
  },
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    // This property is necessary to fix a known bug between
    // material-ui-next Grid component and IE11.
    minHeight: 0,
  },
  footerInfo: {
    display: 'flex',
    alignSelf: 'center',
  },
});

class Form extends PureComponent {
  state = {
    dialogDeleteConfirmation: false,
  };

  handleCancelClick = () => {
    const { backRoute, dataType, onCancel } = this.props;

    if (typeof onCancel === 'function') onCancel();

    if (backRoute) {
      if (backRoute === 'back') {
        browserHistory.goBack();
      } else if (backRoute === 'refresh') {
        browserHistory.go(0);
      } else {
        // Ensure we only have one `/` at the begining of the path
        const path = backRoute.charAt(0) !== '/' ? `/${backRoute}` : backRoute;
        browserHistory.push(path);
      }
    } else if (dataType) {
      browserHistory.push(`/${dataType}`);
    }
  };

  handleDeleteDialogDisplay = (show) => {
    this.setState({ dialogDeleteConfirmation: show });
  };

  handleDeleteConfirmation = () => {
    const { deleteItem, item } = this.props;

    deleteItem(item);

    this.handleDeleteDialogDisplay(false);
  };

  renderDialogConfirmation() {
    const { deleteButtonCancelLabel, deleteButtonSubmitLabel, deleteMessage, deleteTitle, item } = this.props;
    const { dialogDeleteConfirmation } = this.state;

    return (
      <DialogConfirmation
        cancelLabel={deleteButtonCancelLabel || 'No, leave as is'}
        label={deleteMessage || 'Are you sure you wish to remove this?'}
        onCancel={() => this.handleDeleteDialogDisplay(false)}
        onSubmit={() => this.handleDeleteConfirmation(item)}
        submitLabel={deleteButtonSubmitLabel || 'Yes, remove'}
        title={deleteTitle || 'Delete'}
        visible={dialogDeleteConfirmation}
      />
    );
  }

  renderActionSave() {
    const {
      classes,
      customActionsSubmit,
      disabled,
      formName,
      handleSubmit,
      pristine,
      submit,
      submitting,
      submitLabel,
    } = this.props;

    if (customActionsSubmit) return customActionsSubmit;

    return (
      <Button
        color="primary"
        disabled={disabled || pristine || submitting}
        form={formName}
        onClick={handleSubmit((values) => submit(values))}
        type="submit"
        variant="contained"
      >
        {submitLabel || 'Save'}
      </Button>
    );
  }

  renderActionCancel() {
    const { classes, cancelLabel, onCancel, dataType, backRoute } = this.props;

    if (!onCancel && !dataType && !backRoute) return;

    return (
      <Button color="primary" onClick={this.handleCancelClick} type="button" variant="text">
        {cancelLabel || 'Cancel'}
      </Button>
    );
  }

  renderActionReset() {
    const { classes, handleReset, pristine, reset, submitting } = this.props;

    if (!reset && !handleReset) return;

    return (
      <Button
        color="primary"
        disabled={pristine || submitting}
        onClick={reset || handleReset}
        type="button"
        variant="text"
      >
        Reset
      </Button>
    );
  }

  renderActionDelete() {
    const { classes, item, deleteItem, deleteLabel } = this.props;

    // display delete icon only when object have an id field
    if (!item || (!item._id && !item.id) || !deleteItem) return;

    return (
      <Button
        className={classes.deleteButton}
        onClick={() => this.handleDeleteDialogDisplay(true)}
        type="button"
        variant="text"
      >
        {deleteLabel || 'Delete'}
      </Button>
    );
  }

  renderFormFooter() {
    const { classes, customFormActionButtons, footerInfo } = this.props;

    if (customFormActionButtons) {
      return (
        <Paper className={classes.footer} elevation={2} square>
          {customFormActionButtons}
        </Paper>
      );
    }

    return (
      <Paper className={classes.footer} elevation={2} square>
        <div className={classes.buttonsLeft}>{this.renderActionDelete()}</div>
        <div className={classes.footerInfo}>{footerInfo}</div>
        <div className={classes.buttonsRight}>
          {this.renderActionReset()}
          {this.renderActionCancel()}
          {this.renderActionSave()}
        </div>
      </Paper>
    );
  }

  render() {
    const { classes, children, formName, handleSubmit, loading, style, submit, submitting } = this.props;

    if (loading || submitting) return <CircularProgress className={classes.loadingProgress} />;

    return (
      <div className={classes.root}>
        <form
          className={classes.form}
          id={formName}
          onSubmit={handleSubmit((values) => submit(values))}
          style={style || {}}
        >
          {children}
        </form>
        {this.renderFormFooter()}
        {this.renderDialogConfirmation()}
      </div>
    );
  }
}

Form.propTypes = {
  backRoute: PropTypes.string,
  cancelLabel: PropTypes.string,
  children: PropTypes.node.isRequired,
  classes: PropTypes.object.isRequired,
  customActionsSubmit: PropTypes.node,
  customFormActionButtons: PropTypes.node,
  dataType: PropTypes.string,
  deleteButtonCancelLabel: PropTypes.string,
  deleteButtonSubmitLabel: PropTypes.string,
  deleteItem: PropTypes.func,
  deleteLabel: PropTypes.string,
  deleteMessage: PropTypes.string,
  deleteTitle: PropTypes.string,
  disabled: PropTypes.bool,
  formName: PropTypes.string,
  handleReset: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  item: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  loading: PropTypes.bool,
  onCancel: PropTypes.func,
  pristine: PropTypes.bool.isRequired,
  reset: PropTypes.func,
  style: PropTypes.object,
  submit: PropTypes.func.isRequired,
  submitLabel: PropTypes.string,
  submitting: PropTypes.bool.isRequired,
};

export default withStyles(styles)(Form);
